import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useT } from '@/lib/i18n';
import { Navigate, Link } from 'react-router-dom';
import { Users, Film, Scissors, Crown, Activity, ShieldCheck } from 'lucide-react';

const Stat = ({ icon: Icon, label, value }) => (
  <div className="p-6 rounded-2xl border border-white/5 bg-white/[0.02]" data-testid={`admin-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-center justify-between mb-3">
      <span className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-mono">{label}</span>
      <Icon className="w-4 h-4 text-[#D4FF00]" />
    </div>
    <div className="font-heading text-4xl font-black">{value}</div>
  </div>
);

const Admin = () => {
  const { user } = useAuth();
  const { t } = useT();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (user?.role !== 'admin') return;
    api.get('/admin/stats').then((r) => setStats(r.data));
  }, [user]);

  if (user && user.role !== 'admin') return <Navigate to="/dashboard" replace />;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10" data-testid="admin-page">
      <span className="text-xs uppercase tracking-[0.25em] text-[#D4FF00] font-mono">{t('admin.tag')}</span>
      <h1 className="font-heading text-4xl lg:text-5xl font-black mt-2 mb-8 tracking-tighter">{t('admin.title')}</h1>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Stat icon={Users} label={t('admin.stat.users')} value={stats?.total_users ?? '—'} />
        <Stat icon={Film} label={t('admin.stat.videos')} value={stats?.total_videos ?? '—'} />
        <Stat icon={Scissors} label={t('admin.stat.clips')} value={stats?.total_clips ?? '—'} />
        <Stat icon={Crown} label={t('admin.stat.pro')} value={stats?.premium_users ?? '—'} />
      </div>

      <div className="mb-10 flex flex-wrap gap-3">
        <Link
          to="/admin/beta"
          data-testid="admin-beta-link"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[#D4FF00]/30 bg-[#D4FF00]/5 hover:bg-[#D4FF00]/10 text-[#D4FF00] text-xs uppercase tracking-wider font-mono"
        >
          <ShieldCheck className="w-3 h-3" /> Accès bêta · Creator 6 mois
        </Link>
        <Link
          to="/admin/performance"
          data-testid="admin-perf-link"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[#D4FF00]/30 bg-[#D4FF00]/5 hover:bg-[#D4FF00]/10 text-[#D4FF00] text-xs uppercase tracking-wider font-mono"
        >
          <Activity className="w-3 h-3" /> Pipeline performance
        </Link>
      </div>

      <h2 className="font-heading text-2xl font-bold mb-4">{t('admin.recent')}</h2>
      <div className="rounded-2xl border border-white/5 bg-white/[0.02] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-zinc-500 border-b border-white/5">
            <tr><th className="p-4">{t('admin.col.name')}</th><th className="p-4">{t('admin.col.email')}</th><th className="p-4">{t('admin.col.plan')}</th><th className="p-4">{t('admin.col.provider')}</th><th className="p-4">{t('admin.col.joined')}</th></tr>
          </thead>
          <tbody>
            {stats?.recent_users?.map((u) => (
              <tr key={u.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`admin-user-row-${u.id}`}>
                <td className="p-4">{u.name}</td>
                <td className="p-4 text-zinc-400">{u.email}</td>
                <td className="p-4"><span className={`text-xs px-2 py-0.5 rounded ${u.plan === 'pro' ? 'acid-chip' : 'border border-white/10 text-zinc-400'}`}>{u.plan}</span></td>
                <td className="p-4 text-zinc-500 font-mono text-xs">{u.auth_provider}</td>
                <td className="p-4 text-zinc-500 font-mono text-xs">{u.created_at?.slice(0, 10)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Admin;
